import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import React, { useState } from 'react';
import {
  SorterCategory,
  SorterItem
} from '../../../models/LessonComponentContent';

import { ClickAwayListener } from '@mui/material';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Unstable_Grid2';
import { SorterDisplayMode } from '../../../models/LessonComponentSettings';
import { SorterDroppable } from './SorterDroppable';
import { cloneDeep } from 'lodash';
import sorterStyles from '../../../styles/Sorter.module.css';
import { useLessonContext } from '../../../models/LessonContext';

interface SorterCategoryViewProps {
  displayMode: SorterDisplayMode;
  componentkey: number;
  categories: SorterCategory[];
  sourceItems: SorterItem[];
  onMoveItems: (categories: SorterCategory[]) => void;
}

export function SorterCategoryView(props: SorterCategoryViewProps) {
  const { displayMode, componentkey, categories, sourceItems } = props;
  const [dragId, setDragId] = useState<string>();
  const [activeId, setActiveId] = useState<string>();

  const { mode } = useLessonContext();
  const disable = mode === 'mark';

  const source: SorterCategory = {
    id: `source${componentkey}`,
    label: null,
    items: sourceItems
  };

  /**
   * Move an item from one category to another.
   * @param sourceId Drag source *category* (or Source) identifier.
   * @param destinationId Drag destination *category* (or Source) identifier.
   * @param itemId Dragged *item* identifier.
   */
  const moveItem = (
    sourceId: string,
    destinationId: string,
    itemId: string
  ) => {
    const copy = cloneDeep(categories);
    const src = copy.find((cat) => cat.id === sourceId);
    const dst = copy.find((cat) => cat.id === destinationId);
    const item = (src || source).items.find((item) => item.id === itemId);
    if (item) {
      if (src) {
        src.items = src.items.filter((item) => item.id !== itemId);
      }

      if (dst) {
        dst.items.push(item);
      }

      props.onMoveItems(copy);
    }
    setActiveId(undefined);
    setDragId(undefined);
  };

  /**
   * Handle a click on the source stack, either selecting the topmost item, or
   * canceling the selected (active) item.
   * @param _ The category parameter is not used.
   * @param itemId Identifier of topmost item in the Source stack.
   */
  const onSourceClick = (_: string, itemId?: string) => {
    setActiveId(activeId ? undefined : itemId);
  };

  /**
   * Handle a click on a target stack, either dropping the active item into the
   * target, or returning the topmost item to the Source.
   * @param categoryId Identifier of target stack category being clicked.
   * @param itemId Identifier of topmost item in the category stack.
   */
  const onTargetClick = (categoryId: string, itemId?: string) => {
    if (activeId) {
      moveItem(source.id, categoryId, activeId);
    } else if (itemId) {
      moveItem(categoryId, source.id, itemId);
    }
  };

  const onDragEnd = (result: DropResult) => {
    const [sourceId, destinationId, itemId] = [
      result?.source?.droppableId,
      result?.destination?.droppableId,
      result?.draggableId
    ];
    if (sourceId && destinationId && itemId) {
      moveItem(sourceId, destinationId, itemId);
    } else {
      setDragId(undefined);
    }
  };

  const handleClickAway = (event: MouseEvent) => {
    //* It fires with 'touchend' and 'click' event on touch devices.
    //* The 'touchend' fires before 'click',
    if (event.type === 'touchend') {
      return;
    }
    if (activeId) {
      setActiveId(null);
    }
  };

  return (
    <div>
      <DragDropContext
        onDragStart={(initial) => setDragId(initial.draggableId)}
        onDragEnd={(result) => onDragEnd(result)}
      >
        <Grid container spacing={1} alignItems="center" justifyContent="center">
          <ClickAwayListener onClickAway={handleClickAway}>
            <Grid>
              <SorterDroppable
                label="Items"
                category={source}
                displayMode={displayMode}
                activeId={activeId}
                dragId={dragId}
                disable={disable}
                isSource
                onClick={onSourceClick}
              />
            </Grid>
          </ClickAwayListener>

          <Grid className={sorterStyles.dividerContainer}>
            <Divider className={sorterStyles.divider} />
          </Grid>

          <Grid>
            <Grid container justifyContent="center">
              {categories.map((category) => (
                <Grid
                  key={category.id}
                  className={sorterStyles.categorySpacing}
                >
                  <SorterDroppable
                    label={category.label || 'Category'}
                    category={category}
                    displayMode={displayMode}
                    activeId={activeId}
                    dragId={dragId}
                    disable={disable}
                    onClick={onTargetClick}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </DragDropContext>
    </div>
  );
}
